import { computed, ref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import {
  useGetOrganization,
  useListListedParts,
} from "@vendata/power-up-sdk";
import { ledgerCreditsQuery } from "@vendata/power-up-sdk/requests/credits/ledger";
import { fetchOrganizationById } from "@vendata/power-up-sdk/requests/organizations/get-by-id";
import { mineRepairsQuery } from "@vendata/power-up-sdk/requests/repairs/mine";
import { fetchUserById } from "@vendata/power-up-sdk/requests/users/get-by-id";
import type { Repair } from "@vendata/power-up-spec/schemas/Repair";
import { formatPersonName } from "./Repair.logic.ts";

export function useRepairLoader() {
  const openStatus = ref<"open" | "closed" | undefined>("open");
  const closedStatus = ref<"open" | "closed" | undefined>("closed");

  return {
    organizationQuery: useGetOrganization(),
    listedPartsQuery: useListListedParts(),
    ledgerQuery: useQuery(ledgerCreditsQuery()),
    openRepairsQuery: useQuery(mineRepairsQuery({ status: openStatus })),
    closedRepairsQuery: useQuery(mineRepairsQuery({ status: closedStatus })),
  };
}

/** Names for org and submitter columns. Missing lookups stay blank, never ids. */
export function useRepairNameDirectory(repairs: Ref<Repair[]>) {
  const orgIds = computed(() => [
    ...new Set(repairs.value.map((repair) => repair.org_id)),
  ]);
  const userIds = computed(() => [
    ...new Set(repairs.value.map((repair) => repair.created_by_user_id)),
  ]);

  return useQuery({
    queryKey: computed(() => ["repair-directory", orgIds.value, userIds.value]),
    queryFn: async () => {
      const orgNames: Record<string, string> = {};
      const userNames: Record<string, string> = {};
      await Promise.all([
        ...orgIds.value.map(async (id) => {
          try {
            const org = await fetchOrganizationById(id);
            orgNames[id] = org.name;
          } catch {
            // Outside the caller's organization, or not found.
          }
        }),
        ...userIds.value.map(async (id) => {
          try {
            const user = await fetchUserById(id);
            const name = formatPersonName(user.name, "");
            if (name) userNames[id] = name;
          } catch {
            // Person is not in the caller's organization.
          }
        }),
      ]);
      return { orgNames, userNames };
    },
  });
}
